import {html, LitElement} from "lit";
import styles from "../assets/scss/store-locator.scss?inline";
import {customElement, state} from "lit/decorators.js";
import {i18nService} from "../../../services/i18nService";
import {icons} from "../assets/icons/icons"

@customElement('login-dialog')
export class LoginDialog extends LitElement {

  @state() email: string = "";
  @state() password: string = "";
  @state() saveLogin: boolean = false;
  @state() error: boolean = false;
  @state() isEmailValid: boolean = false;

  constructor() {
    super();
    this.onPasswordChanged = this.onPasswordChanged.bind(this)
    this.onEmailChanged = this.onEmailChanged.bind(this)
    this.handleEnterPressed = this.handleEnterPressed.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.handleEscapeKey = this.handleEscapeKey.bind(this)
  }

  async connectedCallback() {
    super.connectedCallback();
    await i18nService.initTranslations()
    this.requestUpdate()
    this.shadowRoot?.getElementById('backdrop')?.addEventListener('click', this.closeModal)
    document.addEventListener('keydown', this.handleEscapeKey)
  }

  handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleEscapeKey);
  }

  onPasswordChanged(event: Event) {
    this.error = false;
    this.password = (event.target as HTMLInputElement).value;
  }

  onEmailChanged(event: Event) {
    this.error = false;
    this.email = (event.target as HTMLInputElement).value;
    const regex = new RegExp('^([a-zA-Z0-9_\\-\\.]+)@([a-zA-Z0-9_\\-\\.]+)\\.([a-zA-Z]{2,5})$');
    this.isEmailValid = regex.test(this.email.toString());
  }

  async handleEnterPressed(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      await this.login(this.email, this.password, false)
    }
  }

  async login(email: string, password: string, saveLogin: boolean) {
    try {
      this.error = false;
      await window.NY_CUSTOMER_SERVICE.login(email, password, saveLogin)
      this.dispatchEvent(new CustomEvent('close'))
    } catch (e) {
      this.error = true
    }
  }

  closeModal() {
    this.error = false
    this.dispatchEvent(new CustomEvent('close'))
  }

  render() {
    return html`
      <style>${styles}</style>
      <div id="backdrop" class="background active"></div>
      <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <button @click=${this.closeModal} class="close-btn" aria-label="${i18nService?.translate("store.dialog.close.btn")}">
          <span aria-hidden="true">${icons.renderCloseCircle()}</span>
        </button>
        <div class="modal-content">
          <div class="headline">
            <div class="error-container" aria-live="polite">
              ${this.error ? html`
                        <span aria-hidden="true">${icons.renderError()}</span>
                        <p class="err" id="modal-title">${i18nService?.translate("store.dialog.error")}</p>` :
                      html`<p id="modal-title">${i18nService?.translate("store.dialog.message")}</p>`}
            </div>
          </div>
          <div class="input-container">
            <label for="input-email">${i18nService?.translate("store.email.placeholder")}</label>
            <input id="input-email" name="email" value="${this.email}" @input="${this.onEmailChanged}"
                   @keyup="${this.handleEnterPressed}" type="email">
            <label for="input-password">${i18nService?.translate("store.password.placeholder")}</label>
            <input id="input-password" name="password" value="${this.password}" @input="${this.onPasswordChanged}"
                   @keyup="${this.handleEnterPressed}" type="password">
          </div>
          <div class="forgot-password-container">
            <a href="https://www.newyorker.de/customer-profile/#/public/customer-profile/reset-password">${i18nService?.translate("store.forgot.password")}</a>
          </div>
          <div class="button-container">
            <a href="https://www.newyorker.de/get-more-club/#/register" class="button register a flex-centered" role="button">
              ${i18nService?.translate("store.register")}
            </a>
            <button class="button login" .disabled=${!(this.isEmailValid && this.password.length)}
                    @click="${() => this.login(this.email, this.password, this.saveLogin)}">
              ${i18nService?.translate("store.login")}
            </button>
          </div>
        </div>
      </div>
    `
  }
}